import React, { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import Sidebar from './Sidebar'
import TrialBanner from '../TrialBanner'

export default function AppShell() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [showInstallBanner, setShowInstallBanner] = useState(false)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isDismissed = localStorage.getItem('installBannerDismissed')
    if (isMobile && !isStandalone && !isDismissed) {
      setShowInstallBanner(true)
    }
  }, [isMobile])

  // Close drawer whenever the screen size changes (desktop doesn't need it)
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false)
  }, [isMobile])

  const handleDismissBanner = () => {
    localStorage.setItem('installBannerDismissed', 'true')
    setShowInstallBanner(false)
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  const isAndroid = /Android/.test(navigator.userAgent)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <TrialBanner />
      <div style={styles.shell}>
      {/* Desktop: persistent sidebar on the left */}
      {!isMobile && <Sidebar />}

      {/* Mobile: slide-in drawer + backdrop, triggered by hamburger button */}
      {isMobile && drawerOpen && (
        <>
          <div style={styles.backdrop} onClick={() => setDrawerOpen(false)} />
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div onClick={() => setDrawerOpen(false)}>
              <Sidebar />
            </div>
          </div>
        </>
      )}

      <div style={styles.main}>
        {/* Mobile top bar with hamburger */}
        {isMobile && (
          <div style={styles.mobileTopBar}>
            <button
              style={styles.hamburger}
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
            >
              ☰
            </button>
            <div style={styles.mobileLogoWrap}>
              <span style={styles.mobileLogoMerq}>Merq</span>
              <span style={styles.mobileLogoLog}>Log</span>
            </div>
            <div style={{ width: '40px' }} /> {/* spacer to keep logo centered */}
          </div>
        )}

        {showInstallBanner && (
          <div style={styles.installBanner}>
            <div style={styles.installLeft}>
              <span style={styles.installIcon}>📲</span>
              <div>
                <div style={styles.installTitle}>Add Merq Log to your home screen</div>
                <div style={styles.installSub}>
                  {isIOS
                    ? 'Tap Share → Add to Home Screen'
                    : isAndroid
                      ? 'Tap Menu → Add to Home Screen'
                      : 'Install for the best experience'
                  }
                </div>
              </div>
            </div>
            <button style={styles.installDismiss} onClick={handleDismissBanner}>✕</button>
          </div>
        )}

        <div style={{
          ...styles.content,
          padding: isMobile ? '16px' : '24px',
        }}>
          <Outlet context={{ isMobile }} />
        </div>
      </div>
      </div>
    </div>
  )
}

const styles = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F7F9FC',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    position: 'relative',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
  },
  // Mobile top bar
  mobileTopBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    background: '#0B1220',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    position: 'sticky',
    top: 0,
    zIndex: 20,
  },
  hamburger: {
    background: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: '6px',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileLogoWrap: {
    fontFamily: "'Fraunces', serif",
    fontSize: '20px',
    fontWeight: 900,
    letterSpacing: '-0.5px',
  },
  mobileLogoMerq: { color: '#fff' },
  mobileLogoLog: { color: '#14B8A6', fontStyle: 'italic' },
  // Drawer
  backdrop: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 90,
    animation: 'fadeIn 0.15s ease-out',
  },
  drawer: {
    position: 'fixed',
    top: 0, left: 0, bottom: 0,
    width: '240px',
    background: '#0B1220',
    zIndex: 100,
    boxShadow: '2px 0 20px rgba(0,0,0,0.3)',
    animation: 'slideIn 0.2s ease-out',
  },
  // Install banner (unchanged from before)
  installBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#1B2B3A',
    padding: '12px 16px',
    gap: '12px',
  },
  installLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  installIcon: { fontSize: '24px', flexShrink: 0 },
  installTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff',
    marginBottom: '2px',
  },
  installSub: {
    fontSize: '12px',
    color: '#2ECC8A',
  },
  installDismiss: {
    background: 'transparent',
    border: 'none',
    color: '#8899AA',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px',
    flexShrink: 0,
  },
}
